// Meta Pixel (Facebook Pixel) Configuration - Simplified for HTML Integration
declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}

export interface MetaPixelEvent {
  eventName: string;
  parameters?: Record<string, any>;
  value?: number;
  currency?: string;
}

class MetaPixelService {
  private static instance: MetaPixelService;
  private isEnabled = false;

  private constructor() {
    // Check if Meta Pixel is loaded (from index.html)
    this.checkPixelAvailability();
  }

  public static getInstance(): MetaPixelService {
    if (!MetaPixelService.instance) {
      MetaPixelService.instance = new MetaPixelService();
    }
    return MetaPixelService.instance;
  }

  /**
   * Check if Meta Pixel is available (loaded from index.html)
   */
  private checkPixelAvailability(): void {
    if (typeof window !== 'undefined' && window.fbq) {
      this.isEnabled = true;
      console.log('Meta Pixel detected and ready for tracking');
    } else {
      // Retry after a short delay
      setTimeout(() => {
        if (typeof window !== 'undefined' && window.fbq) {
          this.isEnabled = true;
          console.log('Meta Pixel detected and ready for tracking');
        }
      }, 1000);
    }
  }

  /**
   * Track an event
   */
  public trackEvent(event: MetaPixelEvent): void {
    if (!this.isEnabled) {
      console.warn('Meta Pixel is not enabled');
      return;
    }

    try {
      const { eventName, parameters, value, currency } = event;
      
      // Handle custom events differently
      if (eventName === 'CustomEvent' && parameters?.event_name) {
        // For custom events, use the event_name parameter as the custom event name
        const customEventName = parameters.event_name;
        const customParameters = { ...parameters };
        delete customParameters.event_name; // Remove event_name from parameters
        
        if (value !== undefined && currency) {
          window.fbq('trackCustom', customEventName, {
            ...customParameters,
            value,
            currency
          });
        } else {
          window.fbq('trackCustom', customEventName, customParameters);
        }
      } else {
        // For standard events
        if (value !== undefined && currency) {
          window.fbq('track', eventName, {
            ...parameters,
            value,
            currency
          });
        } else {
          window.fbq('track', eventName, parameters);
        }
      }

      console.log('Meta Pixel event tracked:', eventName, parameters);
    } catch (error) {
      console.error('Error tracking Meta Pixel event:', error);
    }
  }

  /**
   * Track page view
   */
  public trackPageView(url?: string): void {
    this.trackEvent({
      eventName: 'PageView',
      parameters: url ? { page_url: url } : undefined
    });
  }

  /**
   * Track lead generation
   */
  public trackLead(value?: number, currency: string = 'INR'): void {
    this.trackEvent({
      eventName: 'Lead',
      parameters: {
        content_name: 'MockTrail Landing Page',
        content_category: 'Interview Practice'
      },
      value,
      currency
    });
  }

  /**
   * Track sign up
   */
  public trackSignUp(value?: number, currency: string = 'INR'): void {
    this.trackEvent({
      eventName: 'CompleteRegistration',
      parameters: {
        content_name: 'MockTrail Sign Up',
        content_category: 'Interview Practice'
      },
      value,
      currency
    });
  }

  /**
   * Track free trial start
   */
  public trackStartTrial(value?: number, currency: string = 'INR'): void {
    this.trackEvent({
      eventName: 'StartTrial',
      parameters: {
        content_name: 'MockTrail Free Trial',
        content_category: 'Interview Practice'
      },
      value,
      currency
    });
  }

  /**
   * Track button click
   */
  public trackButtonClick(buttonName: string, location: string): void {
    this.trackEvent({
      eventName: 'CustomEvent',
      parameters: {
        event_name: 'ButtonClick',
        button_name: buttonName,
        location,
        content_name: 'MockTrail Landing Page'
      }
    });
  }

  /**
   * Track form submission
   */
  public trackFormSubmission(formName: string, success: boolean): void {
    this.trackEvent({
      eventName: 'CustomEvent',
      parameters: {
        event_name: 'FormSubmission',
        form_name: formName,
        success,
        content_name: 'MockTrail Landing Page'
      }
    });
  }

  /**
   * Track video interaction
   */
  public trackVideoInteraction(action: 'play' | 'pause' | 'complete', videoName: string): void {
    this.trackEvent({
      eventName: 'CustomEvent',
      parameters: {
        event_name: 'VideoInteraction',
        video_action: action,
        video_name: videoName,
        content_name: 'MockTrail Landing Page'
      }
    });
  }

  /**
   * Track scroll depth
   */
  public trackScrollDepth(depth: number): void {
    this.trackEvent({
      eventName: 'CustomEvent',
      parameters: {
        event_name: 'ScrollDepth',
        scroll_depth: depth,
        content_name: 'MockTrail Landing Page'
      }
    });
  }

  /**
   * Track time on page
   */
  public trackTimeOnPage(seconds: number): void {
    this.trackEvent({
      eventName: 'CustomEvent',
      parameters: {
        event_name: 'TimeOnPage',
        time_seconds: seconds,
        content_name: 'MockTrail Landing Page'
      }
    });
  }

  /**
   * Track pricing view
   */
  public trackPricingView(): void {
    this.trackEvent({
      eventName: 'ViewContent',
      parameters: {
        content_name: 'MockTrail Pricing',
        content_category: 'Pricing',
        content_type: 'pricing_page'
      }
    });
  }

  /**
   * Track ambassador program interest
   */
  public trackAmbassadorInterest(): void {
    this.trackEvent({
      eventName: 'Lead',
      parameters: {
        content_name: 'MockTrail Ambassador Program',
        content_category: 'Ambassador',
        content_type: 'ambassador_signup'
      }
    });
  }

  /**
   * Check if pixel is enabled
   */
  public isPixelEnabled(): boolean {
    return this.isEnabled;
  }
}

// Export singleton instance
export const metaPixelService = MetaPixelService.getInstance();

// Convenience functions for easy usage
export const trackMetaPixelEvent = (event: MetaPixelEvent) => {
  metaPixelService.trackEvent(event);
};

export const trackMetaPixelPageView = (url?: string) => {
  metaPixelService.trackPageView(url);
};

export const trackMetaPixelLead = (value?: number, currency?: string) => {
  metaPixelService.trackLead(value, currency);
};

export const trackMetaPixelSignUp = (value?: number, currency?: string) => {
  metaPixelService.trackSignUp(value, currency);
};

export const trackMetaPixelButtonClick = (buttonName: string, location: string) => {
  metaPixelService.trackButtonClick(buttonName, location);
}; 